/**
 * Extract unique revisions from drawings array
 * @param {Array} drawings - Array of drawing objects
 * @returns {Array} Sorted array of unique revisions
 */
export function getRevisions(drawings) {
  return [...new Set(drawings.map((d) => d.revision))].sort();
}

/**
 * Flatten DrawingDTO[] into comparable material rows
 * Adds metadata to each material (drawing number, sheet, revision, etc.)
 * Identity key: `projectId__dwgNumber__sheetNumber__item`
 *
 * @param {Array} drawings - Array of drawing objects
 * @param {string} filterDwgId - Optional drawing ID filter (softwareId)
 * @param {string} filterSheetId - Optional sheet ID filter (softwareId)
 * @returns {Array} Flattened materials with metadata
 */
export function flattenMaterials(
  drawings,
  filterDwgId = null,
  filterSheetId = null,
) {
  const scope = filterDwgId
    ? drawings.filter((d) => d.softwareId === filterDwgId)
    : drawings;

  return scope.flatMap((d) =>
    d.materials
      .filter(() => !filterSheetId || d.softwareId === filterSheetId)
      .map((m) => ({
        ...m,
        _key: `${d.projectId}__${d.dwgNumber}__${d.sheetNumber}__${m.item || m?.taggedMaterial?.tagNumber || m.materialCode}`,
        _dwgNumber: d.dwgNumber,
        _sheet: `${d.sheetTitle} ${d.sheetNumber}`,
        _revision: d.revision,
        _spool: d.name,
        _project: d.projectId,
        _source: d.source,
      })),
  );
}

/**
 * Compare materials by description
 * Consolidates duplicates and matches similar descriptions
 */
export function compareMaterials(leftMats, rightMats) {
  /**
   * Normalize descriptions for matching
   */
  const normalizeDescription = (value) => {
    return (value || "")
      .toLowerCase()
      .replace(/["']/g, "")
      .replace(/inch/g, "")
      .replace(/in\b/g, "")
      .replace(/,/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  /**
   * Consolidate materials by normalized description
   */
  const consolidate = (materials) => {
    const map = {};

    const cleanNumber = (value) => {
      const num = Number(value);

      return Number.isFinite(num) && num !== 0 ? num : "-";
    };

    const addValues = (a, b) => {
      const n1 = Number(a);
      const n2 = Number(b);

      const valid1 = Number.isFinite(n1) ? n1 : 0;
      const valid2 = Number.isFinite(n2) ? n2 : 0;

      const total = valid1 + valid2;

      return ![0, "0", NaN].includes(total) ? total.toFixed(1) : "-";
    };

    for (const mat of materials) {
      const key = normalizeDescription(mat.description);
      const isBulk = mat.item;

      console.log("matttt", key, mat);
      if (!map[key]) {
        map[key] = {
          ...mat,

          quantity: isBulk
            ? cleanNumber(mat.quantity)
            : cleanNumber(mat.mrqMaterials?.[0]?.procuredQty),
          length: cleanNumber(mat.length || mat?.spec?.dimension),

          ids: [],

          tagNumbers: [
            mat?.taggedMaterial?.tagNumber || mat?.bulkMaterial?.item || "—",
          ],
          materials: [mat],
        };
      } else {
        map[key].length = addValues(
          map[key].length,
          mat.length || mat?.spec?.dimension,
        );

        isBulk &&
          (map[key].quantity = addValues(map[key].quantity, mat.quantity));

        const newTag =
          mat?.taggedMaterial?.tagNumber || mat?.bulkMaterial?.item || "—";
        if (!map[key].tagNumbers.includes(newTag)) {
          map[key].tagNumbers.push(newTag);
        }
        map[key].materials.push(mat);
      }
    }

    return map;
  };

  const lMap = consolidate(leftMats);
  const rMap = consolidate(rightMats);

  const keys = new Set([...Object.keys(lMap), ...Object.keys(rMap)]);

  return Array.from(keys)
    .sort()
    .map((key) => {
      const l = lMap[key] ?? null;
      const r = rMap[key] ?? null;

      let status = "match";
      const diffs = [];

      if (!l) {
        status = "right-only";
      } else if (!r) {
        status = "left-only";
      } else {
        // 1. Fix Quantity Comparison
        const lQty = Number(l.quantity);
        const rQty = Number(r.quantity);

        // Only compare if at least one side has a valid, non-fallback quantity
        if (l.quantity !== "-" || r.quantity !== "-") {
          if (isNaN(lQty) || isNaN(rQty) || lQty !== rQty) {
            diffs.push("quantity");
            status = "mismatch";
          }
        }

        // 2. Fix Length Comparison
        // Only compare if at least one side has a valid length
        if (l.length !== "-" || r.length !== "-") {
          if (l.length !== r.length) {
            diffs.push("length");
            status = "mismatch";
          }
        }
      }

      return {
        key,

        // IMPORTANT:
        // these now BOTH exist in same row
        left: l,
        right: r,

        status,
        diffs,
      };
    });
}

/**
 * Get descriptive label for validation scope based on selection
 * @param {Object} sel - Selection object with project, drawing, sheet properties
 * @returns {string} Label describing the scope (Sheet, Drawing, Project, Plant)
 */
export function getValidateLabel(sel) {
  if (sel.sheet) return "Sheet";
  if (sel.drawing) return "Drawing";
  if (sel.project) return "Project";
  return "Plant";
}

/**
 * Filter projects by material type (based on drawing source)
 * P&ID projects contain drawings with source === "Trimlist"
 * 2D Drawing projects contain drawings with any source !== "Trimlist"
 * Estimation projects contain estimation data (not from drawings)
 *
 * @param {Object} plant - Plant object with projects array
 * @param {string} type - Material type ("P&ID", "2D Drawing", "Estimation")
 * @returns {Array} Filtered array of project objects
 */

export const pnidSources = [
  "Trimlist",
  "Bulklist",
  // "Equipmentlist",
  // "Valvelist",
  // "InstrumentsIndex",
];

export function getFilteredProjects(plant, type) {
  if (!plant?.projects) return [];

  return plant.projects.filter((project) => {
    if (type === "P&ID") {
      // P&ID: has drawings with source === "Trimlist"
      return project.drawings?.some((d) => pnidSources.includes(d.source));
    } else if (type === "2D Drawing") {
      // 2D Drawing: has drawings with any source !== "Trimlist"
      return project.drawings?.some((d) => !pnidSources.includes(d.source));
    } else if (type === "Estimation") {
      // Estimation: has estimation data (separate from drawings)
      return project.estimations && project.estimations.length > 0;
    }
    return false;
  });
}

/**
 * Get all drawings for a specific project, filtered by type
 * P&ID: drawings with source === "Trimlist"
 * 2D Drawing: drawings with any source !== "Trimlist"
 * Estimation: not applicable (comes from separate data)
 *
 * @param {Object} plant - Plant object with projects array
 * @param {string} projectId - Project ID to get drawings from
 * @param {string} type - Material type to filter drawings by source
 * @returns {Array} Filtered drawings array
 */
export function getDrawingsForProject(plant, projectId, type) {
  if (!plant?.projects) return [];

  const project = plant.projects.find((p) => p.id === projectId);
  if (!project?.drawings) return [];

  if (type === "P&ID") {
    // P&ID: only drawings with source === "Trimlist"
    return project.drawings?.filter((d) => pnidSources.includes(d.source));
  } else if (type === "2D Drawing") {
    // 2D Drawing: all drawings with any source !== "Trimlist"
    return project.drawings?.filter((d) => !pnidSources.includes(d.source));
  }

  // Estimation is not drawn from drawings array
  return [];
}

/**
 * Get sheets for a specific drawing
 * A "sheet" is identified by the combination of dwgNumber, revision, and sheetNumber
 *
 * @param {Array} drawings - Array of all drawing objects
 * @param {string} drawingId - Drawing softwareId to get sheets for
 * @param {string} revision - Optional revision filter
 * @returns {Array} Array of sheet objects { id, dwgNumber, sheetNumber, sheetTitle, revision }
 */
export function getSheetsForDrawing(drawings, drawingId, revision = null) {
  if (!drawings?.length) return [];

  const selectedDrawing = drawings.find((d) => d.softwareId === drawingId);
  if (!selectedDrawing) return [];

  const { dwgNumber } = selectedDrawing;
  const revFilter = revision || selectedDrawing.revision;

  const sheets = drawings
    .filter((d) => d.dwgNumber === dwgNumber && d.revision === revFilter)
    .map((d) => ({
      id: d.softwareId,
      dwgNumber: d.dwgNumber,
      sheetNumber: d.sheetNumber,
      sheetTitle: d.sheetTitle,
      revision: d.revision,
    }));

  // Deduplicate by sheetNumber
  const seen = new Set();
  return sheets.filter((s) => {
    const key = `${s.dwgNumber}_${s.sheetNumber}_${s.revision}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Get unique revisions from filtered drawings
 * @param {Array} drawings - Array of drawing objects
 * @returns {Array} Sorted array of unique revisions
 */
export function getRevisionsFromDrawings(drawings) {
  if (!drawings?.length) return [];
  return [...new Set(drawings.map((d) => d.revision))].sort();
}

/**
 * Get children options for the next dropdown level based on current selection
 * Returns hierarchical data for cascading dropdowns
 *
 * @param {Object} plant - Plant object with full hierarchy
 * @param {Object} selection - Current selection { type, project, revision, drawing, sheet }
 * @returns {Object} { projects, revisions, drawings, sheets } - available options at each level
 */
export function getChildrenForSelection(plant, selection) {
  if (!selection?.type) {
    return { projects: [], revisions: [], drawings: [], sheets: [] };
  }

  // Level 1: Get projects filtered by type
  const projects = getFilteredProjects(plant, selection.type);

  // If no project selected, return only projects
  if (!selection.project) {
    return { projects, revisions: [], drawings: [], sheets: [] };
  }

  // Level 2: Get drawings for selected project, filtered by type
  const drawings = getDrawingsForProject(
    plant,
    selection.project,
    selection.type,
  );

  // Get revisions from available drawings
  const revisions = getRevisionsFromDrawings(drawings);

  // If no drawing selected, return projects + drawings + revisions
  if (!selection.drawing) {
    return { projects, drawings, revisions, sheets: [] };
  }

  // Level 3: Get sheets for selected drawing
  const sheets = getSheetsForDrawing(
    drawings,
    selection.drawing,
    selection.revision,
  );

  return { projects, drawings, revisions, sheets };
}

/**
 * Get aggregated materials based on selection depth
 * Progressively narrows scope: Plant -> Project -> Drawing -> Sheet
 * For Estimation, retrieves data from project.estimations instead of drawings
 *
 * @param {Object} plant - Plant object with full hierarchy
 * @param {Object} selection - Selection object { type, project, revision, drawing, sheet }
 * @returns {Array} Flattened materials with metadata matching the selection scope
 */
export function getMaterialsBySelection(plant, selection) {
  if (!selection?.type) return [];

  // Handle Estimation type separately (comes from different data source)
  if (selection.type === "Estimation") {
    if (!plant?.projects) return [];

    let estimations = [];

    // Scope 1: Plant level - all estimations
    if (!selection.project) {
      estimations = plant.projects.flatMap((p) => p.estimations || []);
    }
    // Scope 2: Project level - estimations for selected project
    else {
      const project = plant.projects.find((p) => p.id === selection.project);
      estimations = project?.estimations || [];
    }

    // Flatten estimations similar to materials
    return estimations.flatMap((e) => ({
      ...e,
      _key: `${e.projectId}__estimation__${e.item}`,
      _dwgNumber: "Estimation",
      _sheet: e.category || "—",
      _revision: e.revision || "—",
      _spool: e.description || "—",
      _project: e.projectId,
      _source: "Estimation",
    }));
  }

  // Handle P&ID and 2D Drawing types (from drawings)
  let drawings = [];

  // Scope 1: Plant level - all drawings of the type
  if (!selection.project) {
    if (!plant?.projects) return [];
    drawings = plant.projects.flatMap((p) =>
      getDrawingsForProject(plant, p.id, selection.type),
    );
  }
  // Scope 2: Project level - all drawings in project of the type
  else if (!selection.drawing) {
    drawings = getDrawingsForProject(plant, selection.project, selection.type);
  }
  // Scope 3: Drawing level - specific drawing
  else {
    drawings = getDrawingsForProject(
      plant,
      selection.project,
      selection.type,
    ).filter((d) => d.softwareId === selection.drawing);
  }

  // Apply revision filter if specified
  if (selection.revision) {
    drawings = drawings.filter((d) => d.revision === selection.revision);
  }

  // Apply sheet filter if specified
  return flattenMaterials(drawings, null, selection.sheet);
}

/**
 * Unified data resolver: returns both materials and children options in one call
 * Optimized for component rendering with cascading dropdowns
 *
 * @param {Object} plant - Plant hierarchy object
 * @param {Object} selection - Current selection state
 * @returns {Object} { materials: Array, children: Object }
 */
export function resolveSelectionData(plant, selection) {
  const materials = getMaterialsBySelection(plant, selection);
  const children = getChildrenForSelection(plant, selection);

  return {
    materials,
    children,
  };
}
